/**
 * Login Page - Modern Split-Screen Design
 */

import { Eye, EyeOff } from "lucide-react";
import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { friendlyErrorMessage } from "../utils/apiErrorDetail";
import { ReactComponent as DopplerLogo } from "../assets/doppler_logo.svg";
import { ReactComponent as GoogleIcon } from "../assets/google.svg";
import dopplerGraph from "../assets/login-graph.png";
import { ReactComponent as MicrosoftIcon } from "../assets/microsoft.svg";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/authApi";
import { openOAuthPopup } from "../utils/oauthPopup";
import {
	consumeSessionExpiredNotice,
	RETURN_TO_PARAM,
	safeReturnPath,
	SESSION_EXPIRED_MESSAGE,
} from "../utils/sessionExpiry";
import { defaultLandingPath } from "../utils/featureFlags";
import "./auth-styles.css";

export const Login: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { login, completeOAuthLogin } = useAuth();
	// Where an expired session interrupted the user (DOP-267). Validated, since
	// the param is attacker-supplied.
	const returnTo = safeReturnPath(searchParams.get(RETURN_TO_PARAM));
	// Read once on mount: the notice is one-shot, so a later manual visit to
	// /login does not repeat "your session expired".
	const [sessionExpired] = useState(() => consumeSessionExpiredNotice());
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [oauthLoading, setOauthLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		try {
			await login(email.trim(), password);
			navigate(returnTo ?? defaultLandingPath());
		} catch (err: any) {
			setError(friendlyErrorMessage(err, "Failed to sign in. Please try again."));
		} finally {
			setLoading(false);
		}
	};

	const handleGoogleLogin = async (e?: React.MouseEvent) => {
		e?.preventDefault();
		e?.stopPropagation();

		setError("");
		setOauthLoading(true);
		console.log("[Login] Starting Google OAuth flow...");

		try {
			const result = await openOAuthPopup({
				url: authService.getGoogleOAuthUrl(),
				title: "Login with Google",
			});

			const { token, user } = result;
			console.log(
				"[Login] OAuth success, applying session:",
				token.substring(0, 20) + "..."
			);
			authService.setToken(token);

			let resolvedUser = user;
			if (!resolvedUser) {
				try {
					console.log("[Login] Fetching user profile post-OAuth...");
					resolvedUser = await authService.getCurrentUser();
				} catch (fetchError) {
					console.error(
						"[Login] Failed to fetch user after OAuth:",
						fetchError
					);
					throw new Error("Unable to complete login. Please try again.");
				}
			}

			console.log(
				"[Login] Completing OAuth login with user:",
				resolvedUser.email
			);
			completeOAuthLogin(token, resolvedUser);

			setTimeout(() => {
				console.log("[Login] Navigating to post-login page...");
				navigate(returnTo ?? "/upload");
			}, 100);
		} catch (err: any) {
			console.error("[Login] OAuth error:", err);
			setError(
				friendlyErrorMessage(err, "Google sign-in failed. Please try again."),
			);
		} finally {
			setOauthLoading(false);
		}
	};

	return (
		<div className="auth-container">
			{/* Left Side - Form */}
			<div className="auth-form-side">
				<div className="auth-logo">
					<DopplerLogo className="logo-svg" />
					<span className="logo-text">Doppler</span>
				</div>

				<div className="auth-content">
					<span
						style={{ fontSize: "30px", lineHeight: "50px", fontWeight: "500" }}
					>
						Log in to your account
					</span>

					<form className="auth-form" onSubmit={handleSubmit}>
						{sessionExpired && !error && (
							<div className="auth-notice" role="status">
								<p>{SESSION_EXPIRED_MESSAGE}</p>
							</div>
						)}

						{error && (
							<div className="auth-error">
								<p>{error}</p>
							</div>
						)}

						<div className="form-group">
							<label htmlFor="email" className="form-label">
								Email Address<span className="required">*</span>
							</label>
							<input
								id="email"
								name="email"
								type="email"
								autoComplete="email"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="form-input"
								placeholder="Enter your email address"
							/>
						</div>

						<div className="form-group">
							<div className="form-label-row">
								<label htmlFor="password" className="form-label">
									Password<span className="required">*</span>
								</label>
								<Link to="/forgot-password" className="forgot-link">
									Forgot Password ?
								</Link>
							</div>
							<div className="input-with-icon">
								<input
									id="password"
									name="password"
									type={showPassword ? "text" : "password"}
									autoComplete="current-password"
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className="form-input"
									placeholder="Enter your password"
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="input-icon-btn"
									aria-label={showPassword ? "Hide password" : "Show password"}
								>
									{showPassword ? (
										<EyeOff className="icon" />
									) : (
										<Eye className="icon" />
									)}
								</button>
							</div>
						</div>

						<button type="submit" disabled={loading} className="submit-btn">
							{loading ? "Logging in..." : "Log in"}
						</button>

						<div className="divider">
							<span className="divider-line"></span>
							<span className="divider-text">OR</span>
							<span className="divider-line"></span>
						</div>

						<div className="oauth-buttons">
							<button
								type="button"
								onClick={handleGoogleLogin}
								disabled={oauthLoading}
								className="oauth-btn"
							>
								<GoogleIcon className="oauth-icon" />
								<span>Google Account</span>
							</button>

							<button
								type="button"
								className="oauth-btn"
								disabled
								style={{ opacity: 0.5 }}
							>
								<MicrosoftIcon className="oauth-icon" />
								<span>Microsoft Account</span>
							</button>
						</div>

						<p className="auth-footer-text">
							Don't have an account?{" "}
							<Link to="/signup" className="auth-link">
								Sign up now
							</Link>
						</p>
					</form>
				</div>
			</div>

			{/* Right Side - Background Image */}
			<div className="auth-image-side">
				<span
					style={{
						fontSize: "36.6px",
						lineHeight: "48px",
						color: "#fff",
						background: "transparent",
					}}
				>
					Intelligent investing, built for
					<br />
					private credit
				</span>
				<img
					alt="doppler-graph"
					src={dopplerGraph}
					style={{
						marginTop: "43px",
						height: "100%",
					}}
				/>
			</div>
		</div>
	);
};

export default Login;
